import {
  ProgramAccount,
  ProposalState,
  VoteRecord,
} from "@solana/spl-governance";
import { useAnchorWallet } from "@solana/wallet-adapter-react";
import { FC, useMemo, useState } from "react";
import { isMobile } from "react-device-detect";
import {
  LEFT_POSITION_TOOLTIP_PERCENTAGE,
  LEFT_POSITION_TOOLTIP_PERCENTAGE_MOBILE,
} from "../../../../../common/constants/club.constants";
import { MESSAGE_TYPE } from "../../../../../common/constants/common.constants";
import { ClubType } from "../../../../../common/enums/club.enum";
import { VoteAction } from "../../../../../common/enums/proposals.enums";
import { IClubDataForProposal } from "../../../../../common/interfaces/clubs.interface";
import { IDetailNftData } from "../../../../../common/interfaces/common.interface";
import {
  IBasicProposalData,
  IProposalWithMetaData,
} from "../../../../../common/interfaces/proposals.interface";
import { canMemberVoteOnProposal } from "../../../../../solana/common/helpers/common.helpers";
import { getUsersNfts } from "../../../../../solana/methods/clubs";
import {
  voteToProposal,
  withCastNftVote,
} from "../../../../../solana/methods/proposals";
import { toggleModalOpenClass } from "../../../../../utilities/helpers";
import { createNotification } from "../../../../../utilities/notification";
import NftVotingModal from "../../../../CreateProposalWizard/NftVotingModal/NftVotingModal";
import SubmitButton from "../../../../SubmitButton/SubmitButton";
import Tooltip from "../../../../Tooltip/Tooltip";
import { getColorBasedOnVotesPercentage } from "../proposals-functions";
import "./ProposalsVoting.scss";
import { clubsStore } from "../../../../../contexts/clubStore";

const ProposalsVoting: FC<{
  proposalVotes: any;
  proposalWithMetaData: IProposalWithMetaData;
  clubBasicInfo: IClubDataForProposal;
  isClubMember: boolean;
  userVoteResult: ProgramAccount<VoteRecord> | undefined;
  updateProposalAfterNftVoting?: () => void;
  updateProposalWithMetadata: (proposal: IBasicProposalData) => void;
}> = ({
  proposalVotes,
  clubBasicInfo,
  userVoteResult,
  proposalWithMetaData,
  updateProposalAfterNftVoting,
  updateProposalWithMetadata,
}) => {
  const [isNftVotingModalVisbile, toggleNftVotingModal] = useState(false);

  const [rejecting, toggleRejecting] = useState(false);
  const [accepting, toggleAccepting] = useState(false);

  const { membersNfts } = clubsStore();

  const [isVoteApprove] = useState(true);
  const [showTooltip, toggleShowTooltip] = useState(false);

  const wallet = useAnchorWallet();

  const isVoteButtonDisabled = useMemo(() => {
    return (
      !!userVoteResult ||
      proposalWithMetaData!.proposalBasicData.proposalAccount.account.state !==
        ProposalState.Voting ||
      clubBasicInfo.memberRights?.votingPower === 0 ||
      (clubBasicInfo.clubType === ClubType.TokenProportional &&
        clubBasicInfo.memberRights?.ownership === 0) ||
      (clubBasicInfo.clubType === ClubType.TokenProportional &&
        !canMemberVoteOnProposal(
          clubBasicInfo.memberRights,
          proposalWithMetaData.proposalBasicData.proposalMetadata.createdAt
        ))
    );
  }, [
    userVoteResult,
    clubBasicInfo,
    proposalWithMetaData,
    clubBasicInfo.memberRights,
  ]);

  const showAcceptVotingResults = userVoteResult?.account.vote?.voteType === 0;

  const showRejectVotingResults = userVoteResult?.account.vote?.voteType === 1;

  const getProposalVotingStatus = useMemo(() => {
    if (!userVoteResult) {
      return;
    }
    if (showAcceptVotingResults) {
      return (
        <p className="proposal-voting__vote-status">You voted to Accept</p>
      );
    } else if (showRejectVotingResults) {
      return (
        <p className="proposal-voting__vote-status">You voted to Reject</p>
      );
    } else {
      return null;
    }
  }, [showAcceptVotingResults, showRejectVotingResults, userVoteResult]);

  const castProposalVote = async (
    voteType: VoteAction,
    e?: any,
    cancel?: boolean
  ) => {
    try {
      //TODO@milica-monke-merge

      if (!cancel) {
        voteType === VoteAction.Approve
          ? toggleAccepting(true)
          : toggleRejecting(true);
      }
      e?.stopPropagation();
      await voteToProposal(
        proposalWithMetaData!,
        clubBasicInfo.clubName,
        wallet!,
        clubBasicInfo.treasury,
        voteType,
        clubBasicInfo.clubType,
        clubBasicInfo.mint,
        voteType === VoteAction.Deny && cancel,
        undefined
      );
      updateProposalWithMetadata!(proposalWithMetaData!.proposalBasicData);
      createNotification(MESSAGE_TYPE.SUCCESS, "Sucessfully voted");
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, "Voting failed", error.message);
    } finally {
      if (!cancel) {
        voteType === VoteAction.Approve
          ? toggleAccepting(false)
          : toggleRejecting(false);
      }
    }
  };

  const voteWithNft = async (voteType: VoteAction) => {
    try {
      let nfts: IDetailNftData[] = [];
      if (!clubBasicInfo.isDashboardProposal && membersNfts) {
        nfts = membersNfts;
      } else {
        nfts = await getUsersNfts(wallet!.publicKey, clubBasicInfo.mint);
      }
      voteType === VoteAction.Approve
        ? toggleAccepting(true)
        : toggleRejecting(true);

      await withCastNftVote(
        proposalWithMetaData!,
        clubBasicInfo.clubName,
        wallet!,
        clubBasicInfo.treasury,
        voteType,
        clubBasicInfo.mint,
        nfts,
        clubBasicInfo.clubType
      );
      createNotification(MESSAGE_TYPE.SUCCESS, "Successfully voted!");
      updateProposalAfterNftVoting && updateProposalAfterNftVoting();
    } catch (error: any) {
      console.log(error);
      createNotification(
        MESSAGE_TYPE.ERROR,
        "Problem with voting!",
        error.message
      );
    } finally {
      voteType === VoteAction.Approve
        ? toggleAccepting(false)
        : toggleRejecting(false);
    }
  };

  return (
    <div className="proposal-voting">
      {isNftVotingModalVisbile && updateProposalAfterNftVoting && (
        <NftVotingModal
          clubData={clubBasicInfo}
          votingOption={isVoteApprove}
          closeModal={() => {
            toggleModalOpenClass(false);
            toggleNftVotingModal(false);
          }}
          proposal={proposalWithMetaData}
          updateProposalAfterNftVoting={updateProposalAfterNftVoting}
        />
      )}
      <div className="proposal-voting__slider">
        <Tooltip
          tooltipDescription={`Club approval quorum is set to ${proposalVotes.voteThresholdPct}% in Club configuration`}
          tooltipTitle="Approval quorum"
          leftPositioning={
            isMobile
              ? proposalVotes.voteThresholdPct -
                LEFT_POSITION_TOOLTIP_PERCENTAGE_MOBILE
              : proposalVotes.voteThresholdPct -
                LEFT_POSITION_TOOLTIP_PERCENTAGE
          }
          showTooltip={showTooltip}
          isPercentage
        />
        <div
          className="proposal-voting__label amount"
          style={{ marginLeft: `${proposalVotes.voteThresholdPct! - 2}%` }}
        >
          <p
            className={`proposal-voting__total ${
              proposalVotes.voteThresholdPct === 0 &&
              "proposal-voting__total--not-visible"
            }`}
          >
            {proposalVotes.voteThresholdPct}%
          </p>
          <div
            className="proposal-voting__checkpoint"
            onMouseEnter={() => toggleShowTooltip(true)}
            onMouseLeave={() => toggleShowTooltip(false)}
            style={{
              background: getColorBasedOnVotesPercentage(
                proposalVotes.yesVotePct,
                proposalVotes.noVotePct,
                proposalVotes.voteThresholdPct
              ),
              cursor: "pointer",
              zIndex: 0,
            }}
          />
        </div>
        <div className="proposal-voting__percentage">
          <p className="proposal-voting__approve">
            {proposalVotes.yesVotePct !== 0
              ? proposalVotes.yesVotePct.toFixed(2)
              : proposalVotes.yesVotePct}
            %
          </p>
          <p className="proposal-voting__deny">
            {proposalVotes.noVotePct !== 0
              ? proposalVotes.noVotePct.toFixed(2)
              : proposalVotes.noVotePct}
            %
          </p>
        </div>
        <div className="proposal-voting__lines">
          <div>
            {proposalVotes.voteThresholdPct! > 0 && (
              <div className="proposal-voting__label approve">
                <div
                  className="proposal-voting__checkpoint"
                  style={{
                    background: `${
                      proposalVotes.yesVotePct > 0 ? "#2BFAAF" : "#3B4353"
                    }`,
                    zIndex: 0,
                  }}
                />
              </div>
            )}
          </div>
          <hr
            style={{
              background: "#2BFAAF",
              width: `${proposalVotes.yesVotePct}%`,
            }}
          />
          <hr
            style={{
              background: "#3B4353",
              width: `${
                100 - (proposalVotes.yesVotePct + proposalVotes.noVotePct)
              }%`,
            }}
          />
          <hr
            style={{
              background: "#FD2B2B",
              width: `${proposalVotes.noVotePct}%`,
            }}
          />
          {proposalVotes.voteThresholdPct! < 100 && (
            <div className="proposal-voting__label deny">
              <div
                className="proposal-voting__checkpoint"
                style={{
                  background: `${
                    proposalVotes.noVotePct > 0 ? "#FD2B2B" : "#3B4353"
                  }`,
                  zIndex: 0,
                }}
              />
            </div>
          )}
        </div>
      </div>
      <div className="proposal-voting__voting-actions">
        <SubmitButton
          disabled={isVoteButtonDisabled}
          onClick={() =>
            clubBasicInfo.clubType === ClubType.NftBased
              ? voteWithNft(VoteAction.Approve)
              : castProposalVote(VoteAction.Approve)
          }
          className={`proposal-voting__vote-button ${
            showAcceptVotingResults &&
            "proposal-voting__vote-button proposal-voting__vote-button--voted"
          }`}
          submitting={accepting}
        >
          Accept
        </SubmitButton>
        {getProposalVotingStatus}
        <SubmitButton
          disabled={isVoteButtonDisabled}
          onClick={() =>
            clubBasicInfo.clubType === ClubType.NftBased
              ? voteWithNft(VoteAction.Deny)
              : castProposalVote(VoteAction.Deny)
          }
          className={`proposal-voting__vote-button ${
            showRejectVotingResults &&
            "proposal-voting__vote-button proposal-voting__vote-button--voted"
          }`}
          submitting={rejecting}
        >
          Reject
        </SubmitButton>
      </div>
    </div>
  );
};

export default ProposalsVoting;
